<script>
  import { fade } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import { surveyStore, currentStep } from '../../stores/surveyStore';
  import ProgressBar from './ProgressBar.svelte';
  import EmailCapture from './EmailCapture.svelte';
  import Question from './Question.svelte';
  import ResultsPage from './ResultsPage.svelte';
  import { submitSurvey, saveIntermediate, fetchQuestions, generateTimestamp } from '../../services/api';
  import { onMount } from 'svelte';
  
  // Import the Cavs United logo
  import unitedLogo from '../../assets/images/logos/cha_logo_2.png';


  // Define a loading state
  let isLoading = true;
  let loadError = null;
  let questions = [];

  // Accept props for embedded mode and options
  export let embedded = false;
  export let embedOptions = {};

  // Fetch questions on component mount
  onMount(async () => {
    try {
      isLoading = true;
      questions = await fetchQuestions();
      isLoading = false;

      const params = new URLSearchParams(window.location.search);
      let source = params.get('source') || params.get('utm_source');

      if (!source && document.referrer) {
        try {
          const refParams = new URLSearchParams(new URL(document.referrer).search);
          source = refParams.get('source') || refParams.get('utm_source');
        } catch (e) {
          console.warn('Failed to parse referrer:', document.referrer);
        }
      }

      // Stamp session sk — both reused for all saves this session
      const sk = generateTimestamp();
      surveyStore.update(store => ({
        ...store,
        sk,
        ...(source ? { source } : {})
      }));

      if (source) {
        console.log("Survey source set:", source);
      }
    } catch (error) {
      console.error('Failed to load questions:', error);
      loadError = 'Failed to load survey questions. Please try refreshing the page.';
      isLoading = false;
    }
  });

  let isTransitioning = false;
  
  // Computed value to get current question
  $: currentQuestion = questions[$currentStep];
  $: totalQuestions = questions.length;
  $: isLastQuestion = $currentStep === totalQuestions - 1;
  $: isFirstQuestion = $currentStep === 0;
  $: showResults = $currentStep === totalQuestions;
  
  // Navigate to next question - modified to prevent rapid transitions
  function nextQuestion() {
    if (isTransitioning || isLastQuestion) return;
    
    isTransitioning = true;

    // Fire-and-forget intermediate save on every forward step
    saveIntermediate($surveyStore, currentQuestion, $currentStep);
    
    setTimeout(() => {
      currentStep.update(step => step + 1);
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }, 300);
  }
  
  // Navigate to previous question - also with transition protection
  function prevQuestion() {
    if (isTransitioning || isFirstQuestion) return;
    
    isTransitioning = true;
    
    setTimeout(() => {
      currentStep.update(step => step - 1);
      
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }, 300);
  }
  
  // Submit the survey and show results
  async function handleSubmit() {
    if (isTransitioning) return;
    
    isTransitioning = true;
    currentStep.set(questions.length);
    try {
      console.log('Submitting survey...', $surveyStore);
      await submitSurvey($surveyStore);
      currentStep.set(questions.length);
    } catch (error) {
      console.error('Error submitting survey:', error);
    } finally {
      setTimeout(() => {
        isTransitioning = false;
      }, 400);
    }
  }

  // Direction tracking for better animation
  let direction = 1; // 1 for forward, -1 for backward
  let prevStep = $currentStep;
  
  // Watch for direction changes
  $: {
    if ($currentStep !== prevStep) {
      direction = $currentStep > prevStep ? 1 : -1;
      prevStep = $currentStep;
    }
  }

  // Add a passive touch handler to improve performance for touch events
  // This fixes issues with drag and drop on touch devices
  function addPassiveTouchListeners() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('DOMContentLoaded', () => {
        const options = { passive: true };
        document.addEventListener('touchstart', () => {}, options);
        document.addEventListener('touchmove', () => {}, options);
      });
    }
  }

  // Call this when the component mounts
  addPassiveTouchListeners();

  // Timing variables for transitions - adjusted for smoother experience
  const fadeOutDuration = 300; // Longer fade out time to show selection state
  const fadeInDuration = 400; 
  const delayBetween = 200;
  
</script>

<div class="survey-container {embedded ? 'embedded' : ''}" style={embedded && embedOptions.maxWidth ? `max-width: ${embedOptions.maxWidth}` : ''}>
  {#if isLoading}
    <div class="loading-container card">
      <div class="loading-spinner"></div>
      <p>Loading survey questions...</p>
    </div>
  {:else if loadError}
    <div class="error-container card">
      <p>{loadError}</p>
      <button class="retry-button" on:click={() => window.location.reload()}>Try Again</button>
    </div>
  {:else if !showResults}
    <!-- Move progress bar outside the key block and question-wrapper so it stays visible during transitions -->
    <div class="progress-bar-wrapper">
      <ProgressBar currentStep={$currentStep} totalSteps={totalQuestions - 1} />
    </div>
    
    <div class="question-container mobile-friendly">
      {#key $currentStep}
        <div class="question-wrapper card" 
          in:fade={{ duration: fadeInDuration, delay: fadeOutDuration + delayBetween, easing: quintOut }}
          out:fade={{ duration: fadeOutDuration, easing: quintOut }}
        >
          {#if $currentStep === 1}
            <EmailCapture on:next={nextQuestion} />
          {:else if currentQuestion.type === 'intro'}
            <div class="intro-screen">
              <div class="logo-container">
                <img src={unitedLogo} alt="Cavs United Logo" class="cha_logo_2" />
              </div>
              <h1>{currentQuestion.title}</h1>
              {#if currentQuestion.mainParagraph}
                <p class="main-intro-paragraph">{currentQuestion.mainParagraph}</p>
              {/if}              
              <p class="sub-description">{currentQuestion.description}</p>
              <button class="cta-button" on:click={nextQuestion}>Let's get started</button>
            </div>
          {:else}
            <Question 
              question={currentQuestion} 
              on:answer={nextQuestion} 
              on:submit={handleSubmit} 
              isLast={isLastQuestion}
            />
          {/if}
        </div>
      {/key}
    </div>
    
    <div class="navigation">
      {#if !isFirstQuestion}
        <button class="nav-button prev" on:click={prevQuestion} disabled={isTransitioning}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
          </svg>
          Back
        </button>
      {:else}
        <div></div> <!-- Placeholder to maintain layout -->
      {/if}
      
      {#if !isLastQuestion && $currentStep > 1}
        <button class="nav-button next" on:click={nextQuestion} disabled={isTransitioning}>
          Skip
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path>
          </svg>
        </button>
      {:else if $currentStep > 1}
        <div></div> <!-- Placeholder to maintain layout -->
      {/if}
    </div>
  {:else}
    <ResultsPage />
  {/if}
</div>

<style>
  .survey-container {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
    padding: var(--spacing-md);
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;
    background-color: var(--background-color);
  }
  
  .embedded {
    max-width: 100%;
    padding: 0;
    border-radius: 0;
    background-color: transparent;
    box-shadow: none;
    overflow: visible;
  }
  .embedded .question-wrapper {
    background-color: transparent;
    box-shadow: none;
    border: none;
  }
  
  .embedded .progress-bar-wrapper {
    background-color: transparent;
  }
  
  .embedded .card {
    background-color: transparent;
    box-shadow: none;
    border: none;
  }
  
  .loading-container, .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
    text-align: center;
    margin: var(--spacing-lg) auto;
    max-width: 500px;
  }
  
  .loading-container p, .error-container p {
    margin-top: var(--spacing-md);
    color: var(--text-color);
  }
  
  .retry-button {
    margin-top: var(--spacing-md);
  }
  
  .question-container {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    position: relative;
    margin-top: 0rem; 
  }

  /* Make the progress bar wrapper visible with proper padding */
  .progress-bar-wrapper {
    width: 100%;
    margin: 0 auto var(--spacing-md) auto;
    padding: var(--spacing-xs) var(--spacing-md) 0;
    overflow: visible;
    z-index: 10;
    position: relative;
  }

  .question-wrapper {
    width: 100%;
    max-width: 800px;
    padding: var(--spacing-lg);
    position: relative;
  }
  
  .intro-screen {
    text-align: center;
    padding: var(--spacing-lg);
    display: flex; /* Added flex display */
    flex-direction: column; /* Ensure vertical stacking */
    align-items: center; /* Center align items */
    justify-content: center; /* Center content vertically */
    gap: 1.5rem; /* Add consistent spacing between elements */
  }
  
  /* Logo container styling */
  .logo-container {
    display: flex;
    justify-content: center;
    background: transparent; /* Ensure container has no background */
  }
  
  .cha_logo_2 {
    max-width: 140px;
    height: auto;
    margin-bottom: 0; /* Remove bottom margin, using container margin instead */
    background-color: transparent; /* Ensure the image has transparent background */
    object-fit: contain; /* Maintain aspect ratio */
  }
  
  .intro-screen h1 {
    color: var(--charge-wine);
    margin-bottom: 0rem; /* Reduced from var(--spacing-lg) */
    margin-top: 0rem; /* Added top margin */
  }
  
  .intro-screen p {
    margin-bottom: 0rem; /* Reduced from var(--spacing-xl) */
  }

  .intro-screen .main-intro-paragraph {
    margin-bottom: 0rem; /* Reduced vertical space */
    max-width: 700px; /* Control width */
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5; /* Improved line height for readability */
  }

  .intro-screen .sub-description { /* Style for the paragraph above the button */
    margin-top: 1rem; /* Reduced space above paragraph */
    /* margin-bottom: 2rem;  */
    /* max-width: 500px; Control width */
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5; /* Improved line height for readability */
  }
  
  .cta-button {
    background-color: #6F263D; /* Cavs wine color */
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
    /* margin-top: 1rem;  */
  }
  
  .navigation {
    display: flex;
    justify-content: space-between;
    margin-top: var(--spacing-lg);
    max-width: 800px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
  
  .nav-button {
    display: flex;
    align-items: center;
    background: none;
    border: none;
    color: var(--text-color);
    font-size: 0.9rem;
    cursor: pointer;
    padding: var(--spacing-xs) var(--spacing-md);
    transition: color 0.2s ease;
    box-shadow: none;
  }
  
  .nav-button:hover:not([disabled]) {
    color: var(--charge-wine);
    background: none;
    transform: none;
  }
  
  .nav-button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    background: none;
  }
  
  .prev svg {
    margin-right: var(--spacing-xs);
    fill: currentColor;
  }
  
  .next svg {
    margin-left: var(--spacing-xs);
    fill: currentColor;
  }
  
  /* Mobile-friendly adjustments */
  @media (max-width: 768px) {    
    .survey-container {
      /* padding: var(--spacing-md); */
      padding: 10px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    
    .progress-bar-wrapper {
      padding: var(--spacing-md) var(--spacing-md) 0;
      width: 95%;
      margin: 0 auto var(--spacing-md) auto;
    }
    
    .question-wrapper {
      padding: var(--spacing-md) !important;
      margin: 0 !important;
      width: 100% !important;
      box-sizing: border-box;
    }
    /* Add mobile-friendly class */
    .mobile-friendly {
      padding: 0 var(--spacing-md);
      margin: 0 auto var(--spacing-md) auto;
    }
    
    .intro-screen {
      padding: var(--spacing-md);
      gap: 1rem; /* Reduced gap on mobile */
    }
    
    .cha_logo_2 {
      max-width: 120px; /* Slightly smaller on mobile */
    }
    
    .logo-container {
      /* margin: 1.5rem 0;  */
      padding: 0rem;
    }
    
    .navigation {
      margin-top: var(--spacing-md);
      padding: 0 var(--spacing-md);
    }
    
    .nav-button {
      font-size: 0.8rem;
      padding: var(--spacing-xs) var(--spacing-md);
    }
  }
</style>
